import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loader from '../components/Loader';
import Breadcrumbs from '../components/Breadcrumbs';
import api from '../api';
import styles from './PropertyEmailGuardiansCreate.module.css';

const BlockEmailGuardiansCreate = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [block, setBlock] = useState(null);
  const [guardians, setGuardians] = useState([]);
  const [selectedGuardians, setSelectedGuardians] = useState({});
  const [formData, setFormData] = useState({
    subject: '',
    content: '',
    additionalEmails: [],
    files: []
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/blocks/${id}/emails/guardians`);

      if (response.data.success) {
        setBlock(response.data.block);
        setGuardians(response.data.guardians || []);

        // Pre-select all guardians by default
        const defaultSelected = {};
        (response.data.guardians || []).forEach(guardian => {
          defaultSelected[guardian.id] = true;
        });
        setSelectedGuardians(defaultSelected);
      } else {
        toast.error(response.data.message || 'Failed to load data');
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleGuardianSelection = (guardianId, checked) => {
    setSelectedGuardians(prev => ({
      ...prev,
      [guardianId]: checked
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const addAdditionalEmail = () => {
    setFormData(prev => ({
      ...prev,
      additionalEmails: [...prev.additionalEmails, '']
    }));
  };

  const removeAdditionalEmail = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalEmails: prev.additionalEmails.filter((_, i) => i !== index)
    }));
  };

  const updateAdditionalEmail = (index, value) => {
    setFormData(prev => ({
      ...prev,
      additionalEmails: prev.additionalEmails.map((email, i) =>
        i === index ? value : email
      )
    }));
  };

  const addFile = () => {
    setFormData(prev => ({
      ...prev,
      files: [...prev.files, null]
    }));
  };

  const removeFile = (index) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.filter((_, i) => i !== index)
    }));
  };

  const updateFile = (index, file) => {
    setFormData(prev => ({
      ...prev,
      files: prev.files.map((f, i) => i === index ? file : f)
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Check if at least one guardian is selected
    const selectedCount = Object.values(selectedGuardians).filter(Boolean).length;
    if (selectedCount === 0 && formData.additionalEmails.filter(e => e.trim()).length === 0) {
      newErrors.guardians = 'You have to select at least one guardian or add additional emails!';
    }

    // Validate subject
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }

    // Validate content
    if (!formData.content.trim()) {
      newErrors.content = 'Content is required';
    }

    // Validate additional emails
    formData.additionalEmails.forEach((email, index) => {
      if (email && !isValidEmail(email)) {
        newErrors[`additionalEmails.${index}`] = 'Email address must be in email format';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      // Prepare data
      const submitData = {
        guardians: selectedGuardians,
        additionalEmails: formData.additionalEmails.filter(e => e.trim()),
        subject: formData.subject,
        content: formData.content,
      };

      // Handle file attachments
      if (formData.files.some(f => f)) {
        const attachments = [];
        for (const file of formData.files) {
          if (file) {
            const reader = new FileReader();
            const base64 = await new Promise((resolve) => {
              reader.onload = () => resolve(reader.result.split(',')[1]);
              reader.readAsDataURL(file);
            });
            attachments.push({
              content: base64,
              filename: file.name,
              type: file.type,
            });
          }
        }
        submitData.attachments = attachments;
      }

      const response = await api.post(`/blocks/${id}/emails/guardians`, submitData);

      if (response.data.success) {
        toast.success(response.data.message || 'Email sent successfully');
        navigate(`/blocks/${id}`);
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loader />;
  }

  const breadcrumbs = [
    { label: 'Dashboard', link: '/dashboard' },
    { label: 'Clients', link: '/clients' },
    { label: block?.client_name || `Client #${block?.client_id}`, link: `/clients/${block?.client_id}` },
    { label: 'Blocks', link: '' },
    { label: block?.name || 'Block', link: `/blocks/${id}` },
    { label: 'Email guardians', link: '' },
  ];

  return (
    <div className={styles.container}>
      <Breadcrumbs items={breadcrumbs} />

      <div className={styles.panel} style={{backgroundColor: '#F8F9FA', border:'none'}}>
        <div className={styles.panelHeader}>
          <h3>Email guardians</h3>
        </div>

        <div className={styles.panelBody}>
          <form onSubmit={handleSubmit} id="sendEmailForm">
            <fieldset className={styles.formFieldset}>
              {/* Block Section */}
              <div className={styles.sectionTitle}>
                <h3>Block</h3>
              </div>

              <div className={styles.propertyOverview}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '10px' }}>
                  <Link
                    to={`/blocks/${id}/edit`}
                    style={{
                      padding: '6px 12px',
                      background: '#337ab7',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      marginRight: '10px',
                      fontSize: '13px'
                    }}
                  >
                    Edit
                  </Link>
                  <Link
                    to={`/blocks/${id}/emails/guardians/create`}
                    style={{
                      padding: '6px 12px',
                      background: '#5bc0de',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      fontSize: '13px'
                    }}
                  >
                    Email guardians
                  </Link>
                </div>
                <dl className={styles.propertyList}>
                  <dt>Name:</dt>
                  <dd>{block?.name}</dd>
                  <dt>Client:</dt>
                  <dd>
                    <Link to={`/clients/${block?.client_id}`} style={{ color: '#337ab7' }}>
                      {block?.client_name}
                    </Link>
                  </dd>
                  <dt>Number of properties:</dt>
                  <dd>{block?.properties_count}</dd>
                </dl>
              </div>

              {/* Guardians Section */}
              <div className={styles.sectionTitle}>
                <h3>Guardians</h3>
              </div>

              <div className={styles.guardiansSection}>
                {errors.guardians && (
                  <div className={styles.errorAlert}>
                    {errors.guardians}
                  </div>
                )}

                <table className={styles.guardiansTable}>
                  <thead>
                    <tr>
                      <th>Send email?</th>
                      <th>Name</th>
                      <th>Property</th>
                      <th>Room</th>
                      <th>UGD</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guardians.length > 0 ? (
                      guardians.map(guardian => (
                        <tr key={guardian.id}>
                          <td>
                            <input
                              type="checkbox"
                              id={`guardians[${guardian.id}]`}
                              name={`guardians[${guardian.id}]`}
                              value={guardian.id}
                              checked={selectedGuardians[guardian.id] || false}
                              onChange={(e) => handleGuardianSelection(guardian.id, e.target.checked)}
                            />
                          </td>
                          <td>{guardian.name}</td>
                          <td>{guardian.property_name}</td>
                          <td>{guardian.room_name}</td>
                          <td>{guardian.ugd}</td>
                          <td>{guardian.status}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="6" style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                          No guardians found in this block
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>

                {/* Additional Emails */}
                <div className={styles.additionalEmailsSection}>
                  <label className={styles.additionalEmailsLabel}>
                    Additional emails (send copy)
                  </label>
                  <div className={styles.additionalEmailsContent}>
                    <button
                      type="button"
                      className={styles.addEmailButton}
                      onClick={addAdditionalEmail}
                    >
                      Add email
                    </button>

                    <div className={styles.emailFieldsWrapper}>
                      {formData.additionalEmails.map((email, index) => (
                        <div key={index} className={styles.emailField}>
                          <input
                            type="email"
                            name="emails[]"
                            className={styles.emailInput}
                            value={email}
                            onChange={(e) => updateAdditionalEmail(index, e.target.value)}
                            placeholder="Enter email address"
                          />
                          <button
                            type="button"
                            className={styles.removeButton}
                            onClick={() => removeAdditionalEmail(index)}
                          >
                            Remove
                          </button>
                          {errors[`additionalEmails.${index}`] && (
                            <div className={styles.fieldError}>
                              {errors[`additionalEmails.${index}`]}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Email Section */}
              <div className={styles.sectionTitle}>
                <h3>Email</h3>
              </div>

              <div className={styles.emailSection}>
                {/* Subject */}
                <div className={styles.formGroup}>
                  <label className={styles.requiredLabel}>Subject</label>
                  <div className={styles.inputWrapper}>
                    <input
                      type="text"
                      name="subject"
                      className={styles.textInput}
                      value={formData.subject}
                      onChange={(e) => handleInputChange('subject', e.target.value)}
                      required
                    />
                    {errors.subject && (
                      <div className={styles.fieldError}>{errors.subject}</div>
                    )}
                  </div>
                </div>

                {/* Content */}
                <div className={styles.formGroup}>
                  <label className={styles.requiredLabel}>Content</label>
                  <div className={styles.inputWrapper}>
                    <textarea
                      name="content"
                      className={styles.textareaInput}
                      value={formData.content}
                      onChange={(e) => handleInputChange('content', e.target.value)}
                      rows={10}
                      required
                    />
                    {errors.content && (
                      <div className={styles.fieldError}>{errors.content}</div>
                    )}
                  </div>
                </div>

                {/* Attachments */}
                <div className={styles.formGroup}>
                  <label>Attachments</label>
                  <div className={styles.inputWrapper}>
                    <button
                      type="button"
                      className={styles.addFileButton}
                      onClick={addFile}
                    >
                      Add file
                    </button>

                    <div className={styles.fileFieldsWrapper}>
                      {formData.files.map((file, index) => (
                        <div key={index} className={styles.fileField}>
                          <input
                            type="file"
                            name="files[]"
                            className={styles.fileInput}
                            onChange={(e) => updateFile(index, e.target.files[0])}
                            accept=".doc,.docx,.ppt,.pptx,.xls,.xlsx,.pdf,.jpeg,.bmp,.png,.gif"
                          />
                          <button
                            type="button"
                            className={styles.removeButton}
                            onClick={() => removeFile(index)}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className={styles.submitSection}>
                <button
                  type="submit"
                  className={styles.submitButton}
                  disabled={submitting}
                >
                  {submitting ? 'Sending...' : 'Send email'}
                </button>
              </div>
            </fieldset>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BlockEmailGuardiansCreate;
